import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Receipt, Plus, Edit, Trash2, CheckCircle, Package, Calendar, FileText, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

const PurchasesPage = () => {
  const { getAuthHeaders } = useAuth();
  const [purchases, setPurchases] = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Month/Year filter
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // Dialog states
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showReceiveDialog, setShowReceiveDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [purchaseToReceive, setPurchaseToReceive] = useState(null);
  const [purchaseToDelete, setPurchaseToDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    supplier_name: '',
    supplier_phone: '',
    material_type: '',
    quantity: '',
    unit: 'tonne',
    unit_price: '',
    status: 'pending'
  });

  useEffect(() => {
    fetchRawMaterials();
  }, []);

  useEffect(() => {
    fetchPurchases();
  }, [selectedMonth, selectedYear]);

  const fetchRawMaterials = async () => {
    try {
      const res = await axios.get(`${API}/raw-materials`, getAuthHeaders());
      setRawMaterials(res.data);
    } catch (error) {
      console.error('Error fetching raw materials:', error);
    }
  };

  const fetchPurchases = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/purchases?month=${selectedMonth}&year=${selectedYear}`,
        getAuthHeaders()
      );
      setPurchases(res.data);
    } catch (error) {
      console.error('Error fetching purchases:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchAll = async () => {
    await Promise.all([fetchRawMaterials(), fetchPurchases()]);
  };

  const resetForm = () => {
    setFormData({
      supplier_name: '',
      supplier_phone: '',
      material_type: '',
      quantity: '',
      unit: 'tonne',
      unit_price: '',
      status: 'pending'
    });
  };

  const handleDownloadBC = async (purchase) => {
    try {
      const response = await axios.get(`${API}/purchases/${purchase.id}/pdf`, { ...getAuthHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `AGRABAT_BonCommande_BC-${purchase.id.slice(0, 8).toUpperCase()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Bon de commande téléchargé');
    } catch (error) {
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  const handleWhatsAppBC = async (purchase) => {
    try {
      const response = await axios.get(`${API}/purchases/${purchase.id}/whatsapp-link`, getAuthHeaders());
      window.open(response.data.whatsapp_url, '_blank');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur WhatsApp');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(
        `${API}/purchases`,
        {
          ...formData,
          quantity: parseFloat(formData.quantity),
          unit_price: parseFloat(formData.unit_price)
        },
        getAuthHeaders()
      );
      toast.success('Achat enregistré');
      setShowNewDialog(false);
      resetForm();
      fetchPurchases();
    } catch (error) {
      console.error('Error creating purchase:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = (purchase) => {
    setEditingPurchase(purchase);
    setFormData({
      supplier_name: purchase.supplier_name,
      supplier_phone: purchase.supplier_phone || '',
      material_type: purchase.material_type,
      quantity: purchase.quantity.toString(),
      unit: purchase.unit,
      unit_price: purchase.unit_price.toString(),
      status: purchase.status
    });
    setShowEditDialog(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(
        `${API}/purchases/${editingPurchase.id}`,
        {
          supplier_name: formData.supplier_name,
          supplier_phone: formData.supplier_phone || null,
          material_type: formData.material_type,
          quantity: parseFloat(formData.quantity),
          unit: formData.unit,
          unit_price: parseFloat(formData.unit_price),
          status: formData.status
        },
        getAuthHeaders()
      );
      toast.success('Achat modifié');
      setShowEditDialog(false);
      setEditingPurchase(null);
      resetForm();
      fetchPurchases();
    } catch (error) {
      console.error('Error updating purchase:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    } finally {
      setSaving(false);
    }
  };

  const handleReceive = async () => {
    if (!purchaseToReceive) return;
    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/purchases/${purchaseToReceive.id}/receive`,
        {},
        getAuthHeaders()
      );
      toast.success(response.data.message);
      setShowReceiveDialog(false);
      setPurchaseToReceive(null);
      fetchPurchases();
    } catch (error) {
      console.error('Error receiving purchase:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la réception');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!purchaseToDelete) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/purchases/${purchaseToDelete.id}`, getAuthHeaders());
      toast.success('Achat supprimé');
      setShowDeleteDialog(false);
      setPurchaseToDelete(null);
      fetchPurchases();
    } catch (error) {
      console.error('Error deleting purchase:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-800">En attente</Badge>;
      case 'received':
        return <Badge className="bg-green-100 text-green-800">Reçu</Badge>;
      case 'paid':
        return <Badge className="bg-blue-100 text-blue-800">Payé</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' FCFA';
  };

  // Calculate totals
  const totalPending = purchases.filter(p => p.status === 'pending').reduce((sum, p) => sum + p.total, 0);
  const totalReceived = purchases.filter(p => p.status === 'received').reduce((sum, p) => sum + p.total, 0);

  if (loading) {
    return <div className="flex items-center justify-center h-full">Chargement...</div>;
  }

  return (
    <div className="space-y-6" data-testid="purchases-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-[#1B3268] tracking-tight flex items-center gap-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            <Receipt className="w-10 h-10" />
            Gestion des Achats
          </h1>
          <p className="text-sm text-slate-600 mt-1">Commandes fournisseurs et réception des matières premières</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowNewDialog(true); }}
          className="bg-[#FDC800] hover:bg-[#e6b600] text-[#1B3268] font-bold rounded-sm" 
          data-testid="add-purchase-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouvel Achat
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-amber-50 border border-amber-200 p-5 rounded-sm">
          <p className="text-sm text-amber-700 font-medium">En attente de réception</p>
          <p className="text-2xl font-bold text-amber-800">{formatCurrency(totalPending)}</p>
          <p className="text-xs text-amber-600 mt-1">{purchases.filter(p => p.status === 'pending').length} commande(s)</p>
        </Card>
        <Card className="bg-green-50 border border-green-200 p-5 rounded-sm">
          <p className="text-sm text-green-700 font-medium">Reçus (stocks mis à jour)</p>
          <p className="text-2xl font-bold text-green-800">{formatCurrency(totalReceived)}</p>
          <p className="text-xs text-green-600 mt-1">{purchases.filter(p => p.status === 'received').length} commande(s)</p>
        </Card>
        <Card className="bg-slate-50 border border-slate-200 p-5 rounded-sm">
          <p className="text-sm text-slate-700 font-medium">Total achats</p>
          <p className="text-2xl font-bold text-[#1B3268]">{formatCurrency(totalPending + totalReceived)}</p>
          <p className="text-xs text-slate-500 mt-1">{purchases.length} commande(s) au total</p>
        </Card>
      </div>

      {/* Month/Year Selector */}
      <Card className="p-4 bg-white border border-slate-200 rounded-sm">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium text-slate-700">Mois :</Label>
            <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
              <SelectTrigger className="w-[140px]" data-testid="month-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTHS.map((m, i) => (
                  <SelectItem key={i+1} value={(i+1).toString()}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium text-slate-700">Année :</Label>
            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-[100px]" data-testid="year-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[2024, 2025, 2026, 2027].map(y => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="text-sm text-slate-500 ml-auto">
            {MONTHS[selectedMonth - 1]} {selectedYear}
          </div>
        </div>
      </Card>

      {/* Purchases Table */}
      <Card className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
        <div className="border-b border-slate-100 p-4 bg-slate-50/50">
          <h3 className="text-xl font-semibold text-[#1B3268]">Liste des Commandes</h3>
          <p className="text-sm text-slate-500 mt-1">Validez la réception pour mettre à jour les stocks automatiquement</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-100 text-slate-700 font-semibold uppercase text-xs tracking-wider">
                <th className="p-4 text-left">Date</th>
                <th className="p-4 text-left">Fournisseur</th>
                <th className="p-4 text-left">Matière</th>
                <th className="p-4 text-right">Quantité</th>
                <th className="p-4 text-right">Prix Unit.</th>
                <th className="p-4 text-right">Total</th>
                <th className="p-4 text-center">Statut</th>
                <th className="p-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {purchases.map((purchase) => (
                <tr key={purchase.id} className="hover:bg-slate-50 transition-colors border-b last:border-0" data-testid={`purchase-row-${purchase.id}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      <span className="font-medium">
                        {new Date(purchase.created_at).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                  </td>
                  <td className="p-4 font-medium">{purchase.supplier_name}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4 text-slate-400" />
                      <span>{purchase.material_type}</span>
                    </div>
                  </td>
                  <td className="p-4 text-right font-mono">{purchase.quantity} {purchase.unit}</td>
                  <td className="p-4 text-right font-mono">{formatCurrency(purchase.unit_price)}</td>
                  <td className="p-4 text-right font-mono font-bold text-[#1B3268]">{formatCurrency(purchase.total)}</td>
                  <td className="p-4 text-center">{getStatusBadge(purchase.status)}</td>
                  <td className="p-4">
                    <div className="flex items-center justify-center gap-1">
                      {purchase.status === 'pending' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setPurchaseToReceive(purchase); setShowReceiveDialog(true); }}
                          className="border-green-500 text-green-600 hover:bg-green-50"
                          data-testid={`receive-purchase-${purchase.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Réceptionner
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownloadBC(purchase)}
                        title="Télécharger le Bon de Commande (PDF)"
                        className="text-[#1B3268] hover:bg-slate-100"
                        data-testid={`bc-pdf-${purchase.id}`}
                      >
                        <FileText className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleWhatsAppBC(purchase)}
                        title="Envoyer le BC au fournisseur par WhatsApp"
                        className="text-green-600 hover:bg-green-50"
                        data-testid={`bc-whatsapp-${purchase.id}`}
                      >
                        <MessageCircle className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(purchase)}
                        className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                        data-testid={`edit-purchase-${purchase.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setPurchaseToDelete(purchase); setShowDeleteDialog(true); }}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-purchase-${purchase.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {purchases.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <Receipt className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Aucun achat pour {MONTHS[selectedMonth - 1]} {selectedYear}</p>
            </div>
          )}
        </div>
      </Card>

      {/* New Purchase Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="sm:max-w-[500px]" data-testid="new-purchase-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#1B3268]">Nouvel Achat</DialogTitle>
            <DialogDescription>Créez une commande fournisseur</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label>Fournisseur *</Label>
              <Input
                value={formData.supplier_name}
                onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
                required
                placeholder="Nom du fournisseur"
                data-testid="supplier-name-input"
              />
            </div>
            <div>
              <Label>Téléphone fournisseur (WhatsApp)</Label>
              <Input
                value={formData.supplier_phone}
                onChange={(e) => setFormData({ ...formData, supplier_phone: e.target.value })}
                placeholder="77 123 45 67 ou +223..."
                data-testid="supplier-phone-input"
              />
            </div>
            <div>
              <Label>Type de Matière *</Label>
              <Select value={formData.material_type} onValueChange={(v) => setFormData({ ...formData, material_type: v })}>
                <SelectTrigger data-testid="material-type-select">
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  {rawMaterials.map(m => (
                    <SelectItem key={m.type} value={m.type}>{m.name}</SelectItem>
                  ))}
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
              {formData.material_type === 'autre' && (
                <Input
                  className="mt-2"
                  placeholder="Précisez le type"
                  onChange={(e) => setFormData({ ...formData, material_type: e.target.value })}
                />
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quantité *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  required
                  data-testid="quantity-input"
                />
              </div>
              <div>
                <Label>Unité</Label>
                <Select value={formData.unit} onValueChange={(v) => setFormData({ ...formData, unit: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tonne">Tonne</SelectItem>
                    <SelectItem value="kg">Kg</SelectItem>
                    <SelectItem value="m3">m³</SelectItem>
                    <SelectItem value="sac">Sac</SelectItem>
                    <SelectItem value="unité">Unité</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Prix Unitaire (FCFA) *</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.unit_price}
                onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
                required
                data-testid="unit-price-input"
              />
            </div>
            {formData.quantity && formData.unit_price && (
              <div className="bg-slate-50 p-3 rounded-md">
                <p className="text-sm text-slate-600">Total estimé :</p>
                <p className="text-xl font-bold text-[#1B3268]">
                  {formatCurrency(parseFloat(formData.quantity) * parseFloat(formData.unit_price))}
                </p>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowNewDialog(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={saving} className="bg-[#1B3268] hover:bg-[#152a55] text-white">
                {saving ? 'Enregistrement...' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Purchase Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[500px]" data-testid="edit-purchase-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#1B3268]">Modifier l'Achat</DialogTitle>
            <DialogDescription>Modifiez les informations de la commande</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div>
              <Label>Fournisseur *</Label>
              <Input
                value={formData.supplier_name}
                onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Téléphone fournisseur (WhatsApp)</Label>
              <Input
                value={formData.supplier_phone}
                onChange={(e) => setFormData({ ...formData, supplier_phone: e.target.value })}
                placeholder="77 123 45 67 ou +223..."
                data-testid="edit-supplier-phone-input"
              />
            </div>
            <div>
              <Label>Type de Matière *</Label>
              <Select value={formData.material_type} onValueChange={(v) => setFormData({ ...formData, material_type: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  {rawMaterials.map(m => (
                    <SelectItem key={m.type} value={m.type}>{m.name}</SelectItem>
                  ))}
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quantité *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Unité</Label>
                <Select value={formData.unit} onValueChange={(v) => setFormData({ ...formData, unit: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tonne">Tonne</SelectItem>
                    <SelectItem value="kg">Kg</SelectItem>
                    <SelectItem value="m3">m³</SelectItem>
                    <SelectItem value="sac">Sac</SelectItem>
                    <SelectItem value="unité">Unité</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Prix Unitaire (FCFA) *</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.unit_price}
                onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
                required
              />
            </div>
            {formData.quantity && formData.unit_price && (
              <div className="bg-slate-50 p-3 rounded-md">
                <p className="text-sm text-slate-600">Total :</p>
                <p className="text-xl font-bold text-[#1B3268]">
                  {formatCurrency(parseFloat(formData.quantity) * parseFloat(formData.unit_price))}
                </p>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={saving} className="bg-[#1B3268] hover:bg-[#152a55] text-white">
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Receive Confirmation Dialog */}
      <AlertDialog open={showReceiveDialog} onOpenChange={setShowReceiveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-green-600 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Confirmer la réception ?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 mt-2">
                {purchaseToReceive && (
                  <div className="bg-slate-50 p-3 rounded-md text-sm">
                    <p><strong>Fournisseur :</strong> {purchaseToReceive.supplier_name}</p>
                    <p><strong>Matière :</strong> {purchaseToReceive.material_type}</p>
                    <p><strong>Quantité :</strong> {purchaseToReceive.quantity} {purchaseToReceive.unit}</p>
                    <p><strong>Total :</strong> {formatCurrency(purchaseToReceive.total)}</p>
                  </div>
                )}
                <p className="text-green-600 font-medium">
                  Le stock de matières premières sera automatiquement mis à jour.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReceive}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {saving ? 'Validation...' : 'Confirmer la réception'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Supprimer cet achat ?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 mt-2">
                {purchaseToDelete && (
                  <div className="bg-slate-50 p-3 rounded-md text-sm">
                    <p><strong>Fournisseur :</strong> {purchaseToDelete.supplier_name}</p>
                    <p><strong>Matière :</strong> {purchaseToDelete.material_type}</p>
                    <p><strong>Total :</strong> {formatCurrency(purchaseToDelete.total)}</p>
                  </div>
                )}
                {purchaseToDelete?.status === 'received' && (
                  <p className="text-amber-600 font-medium">
                    Attention : Cette commande a été réceptionnée. Le stock sera ajusté en conséquence.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={saving}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {saving ? 'Suppression...' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PurchasesPage;
